// The dropdown will represent a styled drop down list in a form, specifically for phone numbers with country codes.
// It is intended to be inside the field-container of a regular input field.
import Vue from 'vue'
import LibPhoneNumber from 'google-libphonenumber'

// Many regions have the same prefix. For that reason, we'd get multiple flags for the same code if we allowed all
// through. For that reason, we'll exclude some regions completely, that are most likely not going to be included.
const excludedRegions = [
  // +1 (use US)
  'AG',
  'AI',
  'AS',
  'BB',
  'BM',
  'BS',
  'CA',
  'DM',
  'DO',
  'GD',
  'GU',
  'JM',
  'KN',
  'KY',
  'LC',
  'MP',
  'MS',
  'PR',
  'SX',
  'TC',
  'TT',
  'VC',
  'VG',
  'VI',

  // +7 (use RU)
  'KZ',

  // +39 (use IT)
  'VA',

  // +44 (use GB)
  'GG',
  'IM',
  'JE',

  // +47 (use NO)
  'SJ',

  // +61 (use AU)
  'CC',
  'CX',

  // +212 (use MA - Morocco)
  'EH'

  /* Ignore these, obscure countries.
  +262: (2) ["RE", "YT"]
  +290: (2) ["SH", "TA"]
  +358: (2) ["AX", "FI"]
  +590: (3) ["BL", "GP", "MF"]
  +599: (2) ["BQ", "CW"]
  */
]

export default {
  name: 'dropdown-phone-prefix',

  props: {
    // The human readable name of the field. The component won't attempt to translate this, it expects a ready value.
    title: '',

    // Any additional classes to add to the drop down container, space separated.
    classes: '',

    // The value to show in the field before a value has been selected.
    placeholder: '',

    // If you have a number of input fields in a row (this drop down being one of them), but you don't want a title on
    // it, set this to true to force a title placeholder so the drop down isn't offset in height from the other fields
    // in the row.
    forceTitle: false,

    region: '',

    // Set the input of the phone field here. It's used to automatically define which region we've selected.
    phoneNumber: ''
  },

  data () {
    return {
      isShowing: false,
      filter: '',

      // Defaults to Sweden.
      selectedRegion: this.region ? this.region.region.toUpperCase() : 'SE',
      selectedPrefix: this.region ? this.region.prefix : '+46'
    }
  },

  computed: {
    // phoneCountries returns an array of objects in the following format:
    // {
    //  region: 'SE',
    //  prefix: '+46'
    // }
    values () {
      let regions = LibPhoneNumber.PhoneNumberUtil.getInstance().getSupportedRegions()
      let ret = []
      let self = this
      regions.forEach(function (region) {
        // Ignore doubles we want to omit.
        if (excludedRegions.indexOf(region) !== -1) {
          return
        }

        let item = {
          region: region,
          prefix: '+' + LibPhoneNumber.PhoneNumberUtil.getInstance().getCountryCodeForRegion(region),
          preSelected: region.toUpperCase() === self.selectedRegion
        }

        // See if we've filtered out regions.
        if (self.filter !== '') {
          if (item.prefix.indexOf(self.filter) === -1 && item.region.toLowerCase().indexOf(self.filter.toLowerCase()) === -1) {
            return
          }
        }

        ret.push(item)
      })
      return ret
    },

    selectedValue () {
      console.log('calculating selected value')

      let ret = null
      this.values.forEach(function (value) {
        if (value.preSelected) {
          ret = value
        }
      })
      return ret === null ? this.values[0] : ret
    },

    disabled () {
      // Disallow changing the region if you've entered one in the phone number itself, since that's what'll take
      // precedence when submitting.
      switch (true) {
        case this.phoneNumber.length > 0 && this.phoneNumber.substr(0, 1) === '+':
        case this.phoneNumber.length > 1 && this.phoneNumber.substr(0, 2) === '00':
          return true
      }
      return false
    }
  },

  mounted () {
    // Check if we have a value we want to set as default instead of the first one, which we'll use as the fallback.
    let preSelectedfound = false
    this.values.forEach(function (item) {
      if (item.preSelected && item.preSelected === true) {
        preSelectedfound = true
      }
    })

    let self = this
    if (!preSelectedfound) {
      this.values.forEach(function (item) {
        if (item.region === self.selectedRegion) {
          item.preSelected = true
        }
      })
    }
  },

  // Close the drop down when clicking the document.
  created () {
    document.addEventListener('click', this.documentClickHandler)
  },

  // Cleanup.
  destroyed () {
    document.removeEventListener('click', this.documentClickHandler)
  },

  methods: {
    select (v) {
      this.selectedRegion = v.region
      this.selectedPrefix = v.prefix

      this.isShowing = false
      this.$emit('valueSelected', v)
    },

    documentClickHandler () {
      this.isShowing = false
    },

    getFlagSrc (region) {
      // @todo LibPhoneNumber regions we don't have flags for. How to handle?
      let flag = region.toLowerCase()
      switch (flag) {
        case 'ac':
        case 'cv':
        case 'eh':
        case 'gf':
        case 'gp':
        case 'gy':
        case 'jm':
        case 'ki':
        case 'mf':
        case 'nc':
        case 'pm':
        case 'qa':
        case 're':
        case 'sh':
        case 'sj':
        case 'sz':
        case 'ta':
        case 'tv':
        case 'wf':
        case 'yt':
          flag = 'eu'
          break
      }
      return require('../../static/flags/' + flag + '.svg')
    }
  },

  watch: {
    // phoneNumber watches the number the user filled in and updates the region accordingly.
    phoneNumber (pn) {
      // Normalize double zero to a plus.
      if (pn.length > 2 && pn.substr(0, 2) === '00') {
        pn = '+' + pn.substr(2)
      }

      let self = this
      this.values.forEach(function (item) {
        if (pn.length >= item.prefix.length && pn.substr(0, item.prefix.length) === item.prefix) {
          Vue.set(self, 'selectedRegion', item.region)
          Vue.set(self, 'selectedPrefix', item.prefix)
        }
      })
      this.$emit('regionSelected', { region: this.selectedRegion, prefix: this.selectedPrefix })
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/dropdown-phone-prefix/script.js